import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X, Check, Users } from 'lucide-react'
import { deptColor, INTERNAL_DIVISIONS, BUSINESS_UNITS } from '@/lib/departments'

export interface Person {
  email: string
  username: string
  department: string | null
}

/**
 * Pemilih orang: ketik untuk mencari nama, pilih beberapa sekaligus, dikelompokkan
 * per divisi / unit bisnis supaya daftar panjang tetap terbaca. Dipakai oleh penerima
 * tiket. Sengaja satu komponen agar bentuknya konsisten di semua tempat.
 */
export function PeoplePicker({
  value,
  onChange,
  exclude = [],
  placeholder = 'Cari nama atau email',
  max = 20,
}: {
  value: string[]
  onChange: (emails: string[]) => void
  /** Email yang tidak boleh dipilih (biasanya diri sendiri). */
  exclude?: string[]
  placeholder?: string
  max?: number
}) {
  const [people, setPeople] = useState<Person[]>([])
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/users/all')
      .then((r) => (r.ok ? r.json() : []))
      .then((d: Person[]) => { if (alive) setPeople(Array.isArray(d) ? d : []) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Tutup saat klik di luar.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const ex = useMemo(() => new Set(exclude.map((e) => e.toLowerCase())), [exclude])
  const selected = useMemo(() => new Set(value.map((e) => e.toLowerCase())), [value])
  const byEmail = useMemo(() => new Map(people.map((p) => [p.email.toLowerCase(), p])), [people])

  // Urutan grup: divisi internal dulu (paling sering dituju), lalu unit bisnis, lalu sisanya.
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const pool = people.filter((p) => {
      if (ex.has(p.email.toLowerCase())) return false
      if (!needle) return true
      return p.username.toLowerCase().includes(needle) || p.email.toLowerCase().includes(needle)
    })
    const order = ['HOLDING', ...INTERNAL_DIVISIONS, ...BUSINESS_UNITS] as string[]
    const map = new Map<string, Person[]>()
    for (const p of pool) {
      const k = p.department ?? 'Tanpa departemen'
      map.set(k, [...(map.get(k) ?? []), p])
    }
    return [...map.entries()].sort((a, b) => {
      const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0])
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
    })
  }, [people, q, ex])

  const toggle = (email: string) => {
    const lc = email.toLowerCase()
    if (selected.has(lc)) onChange(value.filter((e) => e.toLowerCase() !== lc))
    else if (value.length < max) onChange([...value, email])
  }

  return (
    <div ref={boxRef} className="relative">
      {/* Chip terpilih */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((e) => {
            const p = byEmail.get(e.toLowerCase())
            const c = deptColor(p?.department ?? null)
            return (
              <span key={e}
                className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg border text-[11px] font-body font-semibold"
                style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
                {p?.username ?? e}
                <button type="button" onClick={() => toggle(e)}
                  className="p-0.5 rounded hover:bg-black/10 transition-colors" title="Hapus">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C0BDB6] pointer-events-none" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E8E7E3] rounded-xl text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#1A1916] transition-colors"
        />
      </div>

      {open && (
        <div className="absolute z-40 mt-1.5 w-full max-h-72 overflow-y-auto bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5">
          {groups.length === 0 && (
            <p className="px-3 py-3 text-xs font-body text-[#A8A8A2]">Tidak ada yang cocok.</p>
          )}
          {groups.map(([dept, list]) => {
            const c = deptColor(dept)
            return (
              <div key={dept}>
                <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.text }} />
                  <span className="text-[10px] font-body font-bold uppercase tracking-wider text-[#8A8A85]">{dept}</span>
                  <span className="text-[10px] text-[#C8C7BF]">{list.length}</span>
                </div>
                {list.map((p) => {
                  const on = selected.has(p.email.toLowerCase())
                  return (
                    <button key={p.email} type="button" onClick={() => toggle(p.email)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-[#F7F6F3] transition-colors text-left">
                      <span className="min-w-0">
                        <span className="block text-sm font-body text-[#1A1916] truncate">{p.username}</span>
                        <span className="block text-[11px] font-body text-[#A8A8A2] truncate">{p.email}</span>
                      </span>
                      {on && <Check className="w-4 h-4 text-[#1A1916] flex-shrink-0" />}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}

      <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-body text-[#A8A8A2]">
        <Users className="w-3 h-3" />
        {value.length === 0 ? 'Belum ada penerima dipilih' : `${value.length} penerima dipilih`}
        {value.length >= max && ' (maksimal tercapai)'}
      </p>
    </div>
  )
}
